import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import axios from 'axios';
import useSEO from '../../hooks/useSEO';
import './BuyerAuth.css';

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra',
  'Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim',
  'Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi'
];

const VEHICLE_CATEGORIES = ['Cars', 'Mini Vans', 'Buses'];

const BuyerRegister = () => {
  useSEO({ title: 'Create Buyer Account', noindex: true });
  const { getAuthHeader, API_URL, refreshBuyer } = useBuyerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from || '/';
  // Arriving from the "Register as a dealer" CTA — start them on the dealer
  // branch rather than making them find the radio they already clicked.
  const wantsDealer = searchParams.get('type') === 'dealer';

  const [form, setForm] = useState({
    name: '', dob: '', gender: '', alt_phone: '', address: '',
    city: '', state: '', pincode: '', id_proof_type: '', id_proof_number: '',
    buyer_type: wantsDealer ? 'dealer' : 'personal', business_name: '', dealer_categories: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const isDealer = form.buyer_type === 'dealer';

  const toggleCategory = (category) => setForm(prev => ({
    ...prev,
    dealer_categories: prev.dealer_categories.includes(category)
      ? prev.dealer_categories.filter(c => c !== category)
      : [...prev.dealer_categories, category]
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.state) {
      setError('Name, city, and state are required');
      return;
    }
    if (isDealer && !form.business_name.trim()) {
      setError('Business name is required for a dealer account');
      return;
    }
    if (isDealer && !form.dealer_categories.length) {
      setError('Select at least one vehicle type you deal in');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/buyer/register`, form, { headers: getAuthHeader() });
      setSuccess(true);
      await refreshBuyer();
      setTimeout(() => navigate(from), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="register-page" data-testid="register-success">
        <div className="register-form" style={{ textAlign: 'center', padding: '60px' }}>
          <h2 style={{ color: '#388e3c', marginBottom: '12px' }}>
            {isDealer ? 'Dealer Account Ready!' : 'Profile Complete!'}
          </h2>
          <p>You can now enquire about vehicles. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page" data-testid="buyer-register-page">
      <h1>Complete Your Profile</h1>
      <p className="subtitle">We need a few details to process your enquiries</p>

      {error && <div className="auth-error">{error}</div>}

      <form className="register-form" onSubmit={handleSubmit}>
        {/* First question on the form: everything sales does with this record
            depends on the answer, and a dealer will not volunteer it later. */}
        <fieldset className="buyer-type-choice" data-testid="buyer-type-choice">
          <legend>Who are you buying for? *</legend>
          <div className="buyer-type-options">
            <label className={`buyer-type-card ${!isDealer ? 'selected' : ''}`}>
              <input
                type="radio"
                name="buyer_type"
                value="personal"
                checked={!isDealer}
                onChange={() => set('buyer_type', 'personal')}
                data-testid="buyer-type-personal"
              />
              <span className="buyer-type-title">Personal use</span>
              <span className="buyer-type-hint">Buying a vehicle to drive or run myself</span>
            </label>
            <label className={`buyer-type-card ${isDealer ? 'selected' : ''}`}>
              <input
                type="radio"
                name="buyer_type"
                value="dealer"
                checked={isDealer}
                onChange={() => set('buyer_type', 'dealer')}
                data-testid="buyer-type-dealer"
              />
              <span className="buyer-type-title">Dealer</span>
              <span className="buyer-type-hint">Buying to resell, or for a fleet or dealership</span>
            </label>
          </div>
        </fieldset>

        {isDealer && (
          <div className="dealer-fields" data-testid="dealer-fields">
            <div className="form-group">
              <label>Business / Dealership Name *</label>
              <input
                type="text"
                value={form.business_name}
                onChange={(e) => set('business_name', e.target.value)}
                placeholder="e.g. SS Motors"
                data-testid="reg-business-name"
              />
            </div>
            <div className="form-group">
              <label>Which vehicles do you deal in? *</label>
              <div className="category-checks" data-testid="reg-dealer-categories">
                {VEHICLE_CATEGORIES.map(category => (
                  <label
                    key={category}
                    className={`category-check ${form.dealer_categories.includes(category) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={form.dealer_categories.includes(category)}
                      onChange={() => toggleCategory(category)}
                      data-testid={`reg-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    {category}
                  </label>
                ))}
              </div>
              <small>We will tell you first when stock in these categories comes in.</small>
            </div>
          </div>
        )}

        <div className="form-grid">
          <div className="form-group">
            <label>Full Name *</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="reg-name" />
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} data-testid="reg-dob" />
          </div>
          <div className="form-group">
            <label>Gender</label>
            <select value={form.gender} onChange={(e) => set('gender', e.target.value)} data-testid="reg-gender">
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Alternate Phone (optional)</label>
            <input type="tel" value={form.alt_phone} onChange={(e) => set('alt_phone', e.target.value)} data-testid="reg-alt-phone" />
          </div>
          <div className="form-group full-span">
            <label>Full Address</label>
            <textarea value={form.address} onChange={(e) => set('address', e.target.value)} rows={2} data-testid="reg-address" />
          </div>
          <div className="form-group">
            <label>City *</label>
            <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} required data-testid="reg-city" />
          </div>
          <div className="form-group">
            <label>State *</label>
            <select value={form.state} onChange={(e) => set('state', e.target.value)} required data-testid="reg-state">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Pincode</label>
            <input type="text" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} data-testid="reg-pincode" />
          </div>
          <div className="form-group">
            <label>ID Proof Type</label>
            <select value={form.id_proof_type} onChange={(e) => set('id_proof_type', e.target.value)} data-testid="reg-id-type">
              <option value="">Select</option>
              <option value="Aadhar">Aadhar</option>
              <option value="PAN">PAN</option>
              <option value="Passport">Passport</option>
              <option value="Driving Licence">Driving Licence</option>
            </select>
          </div>
          <div className="form-group">
            <label>ID Proof Number</label>
            <input type="text" value={form.id_proof_number} onChange={(e) => set('id_proof_number', e.target.value)} data-testid="reg-id-number" />
          </div>
        </div>
        <button type="submit" className="register-submit" disabled={loading} data-testid="complete-registration">
          {loading ? 'Saving...' : 'Complete Registration'}
        </button>
      </form>
    </div>
  );
};

export default BuyerRegister;
